/**
 * Typed date/time text → the canonical field value (POS2-1068). The Filters popover accepted
 * only "yyyy-MM-dd" and "HH:MM", so "20260707" and "1111" — both natural things to type into a
 * field showing a date — were marked invalid and dropped. Kept pure so the accepted forms are
 * unit-testable and the Filters popover and the dashboard Custom range can't drift apart.
 */
import { format } from "date-fns";

/**
 * The earliest day any custom range accepts (POS2-1279). A native `<input type="date">` has no
 * floor of its own and `parseDay` takes any four-digit year, so "1940" was as committable as
 * "2026" — by picker or by typing — and produced a ~31,300-day period that collapsed the spend
 * target to nothing rather than erroring.
 *
 * **Global rather than per site, deliberately.** The codebase has an authoritative per-site
 * earliest date in `site_went_live_on`, but two of the three call sites run org-wide with no site
 * selected, where it has no single answer, and it is null for a site with no waste events yet —
 * so a constant would be needed underneath it in any case. Approximate, and the right shape of
 * approximate: it exists to keep nonsense years out, not to bound each site to its own data.
 */
export const EARLIEST_DAY = "2022-01-01";

/** The floor's year, for the message that says why a date was refused. */
export const EARLIEST_YEAR = EARLIEST_DAY.slice(0, 4);

/** True when a canonical "yyyy-MM-dd" falls before the floor. An empty field means "the page's
 * default", never "unbounded" (POS2-1049), so it is not out of range. */
export function isBeforeEarliest(day: string): boolean {
  return day !== "" && day < EARLIEST_DAY;
}

/** Canonical "yyyy-MM-dd" for a typed date, or null when the text isn't one. */
export function parseDay(raw: string): string | null {
  const text = raw.trim();

  // Year-first, with or without separators: 2026-07-07, 20260707.
  const yearFirst = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text) ?? /^(\d{4})(\d{2})(\d{2})$/.exec(text);
  if (yearFirst) return buildDay(+yearFirst[1], +yearFirst[2], +yearFirst[3]);

  // Day-first, the order the app already displays (30.07.2026): 7/7/2026, 07.07.2026.
  const dayFirst = /^(\d{1,2})[./](\d{1,2})[./](\d{4})$/.exec(text);
  if (dayFirst) return buildDay(+dayFirst[3], +dayFirst[2], +dayFirst[1]);

  return null;
}

/** Rejects rollovers — `new Date(2026, 12, 40)` is a real date, just not the one asked for. */
function buildDay(year: number, month: number, day: number): string | null {
  const date = new Date(year, month - 1, day);
  const round = date.getFullYear() === year && date.getMonth() === month - 1;
  return round && date.getDate() === day ? format(date, "yyyy-MM-dd") : null;
}

/** Canonical 24-hour "HH:MM" for a typed time, or null when the text isn't one. */
export function parseTime(raw: string): string | null {
  const match = /^(\d{1,2}):?(\d{2})$/.exec(raw.trim());
  if (!match) return null;
  const hours = +match[1];
  const minutes = +match[2];
  if (hours > 23 || minutes > 59) return null;
  return `${String(hours).padStart(2, "0")}:${match[2]}`;
}
